import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { ArrowRight, Sparkles } from 'lucide-react';

const steps = [
  {
    title: "Welcome to MathTopper!",
    subtitle: "Your daily math companion",
    emoji: "🦉",
    description: "Meet Zeno, your AI math tutor. Practice daily, build streaks, and become a math champion!",
    bg: "linear-gradient(135deg, #FFF8F0 0%, #FFE8D6 100%)"
  },
  {
    title: "Tell us about you",
    subtitle: "So we can personalize your experience",
    emoji: "🎓",
    isForm: true,
    bg: "linear-gradient(135deg, #FFF8F0 0%, #E8F4FD 100%)"
  },
  {
    title: "You're all set!",
    subtitle: "Your first practice starts now",
    emoji: "🚀",
    description: "Complete 10 questions daily to build your streak. Earn MathCoins for every correct answer!",
    bg: "linear-gradient(135deg, #FFF8F0 0%, #E8FDF5 100%)"
  }
];

export default function OnboardingFlow() {
  const { user, profile, token, API, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [name, setName] = useState(user?.name || '');
  const [classLevel, setClassLevel] = useState(profile?.class_level || 8);

  const completeOnboarding = async () => {
    try {
      await axios.post(`${API}/student/onboarding/complete`, {
        name, class_level: classLevel
      }, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true });
      await refreshUser();
      navigate('/home', { replace: true });
    } catch {
      navigate('/home', { replace: true });
    }
  };

  const current = steps[step];

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6" style={{ background: current.bg }}>
      {/* Progress dots */}
      <div className="flex gap-2 mb-8">
        {steps.map((_, i) => (
          <div key={i} className="h-2 rounded-full transition-all duration-300"
            style={{ width: i === step ? 32 : 8, background: i === step ? '#FF6B35' : '#E5DACE' }} />
        ))}
      </div>

      <div className="w-full max-w-sm text-center animate-slide-up" key={step}>
        <div className="text-7xl mb-6" style={{ filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.1))' }}>{current.emoji}</div>
        <h1 className="font-fredoka text-3xl mb-2" style={{ color: '#1B4F8A' }}>{current.title}</h1>
        <p className="text-sm font-semibold mb-6" style={{ color: '#8B7355' }}>{current.subtitle}</p>

        {current.isForm ? (
          <div className="space-y-4 text-left mb-8">
            <div>
              <label className="text-xs font-bold block mb-1" style={{ color: '#1B4F8A' }}>Your Name</label>
              <input data-testid="onboarding-name" value={name} onChange={e => setName(e.target.value)}
                className="w-full bg-white border-2 rounded-2xl px-4 py-3 font-bold text-sm outline-none focus:border-[#FF6B35] transition-all"
                style={{ borderColor: '#E5DACE', color: '#1B4F8A' }} placeholder="Enter your name" />
            </div>
            <div>
              <label className="text-xs font-bold block mb-2" style={{ color: '#1B4F8A' }}>Your Class</label>
              <div className="flex gap-2">
                {[6, 7, 8, 9, 10].map(c => (
                  <button key={c} data-testid={`class-${c}`} onClick={() => setClassLevel(c)}
                    className="flex-1 py-3 rounded-2xl font-extrabold text-sm border-2 transition-all btn-press"
                    style={{
                      background: classLevel === c ? '#FF6B35' : 'white',
                      borderColor: classLevel === c ? '#FF6B35' : '#E5DACE',
                      color: classLevel === c ? 'white' : '#1B4F8A',
                      boxShadow: classLevel === c ? '0 3px 0 #C04918' : '0 3px 0 #E5DACE'
                    }}>
                    {c}
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <p className="text-sm font-semibold mb-8 leading-relaxed" style={{ color: '#64748B' }}>{current.description}</p>
        )}

        {/* Highlights for last step */}
        {step === 2 && (
          <div className="grid grid-cols-3 gap-3 mb-8">
            {[
              { icon: '🔥', label: 'Daily Streaks' },
              { icon: '🪙', label: '50 Coins/Day' },
              { icon: '🦉', label: 'AI Tutor' },
            ].map((h, i) => (
              <div key={i} className="bg-white border-2 rounded-2xl p-3 text-center" style={{ borderColor: '#E5DACE' }}>
                <div className="text-2xl mb-1">{h.icon}</div>
                <p className="text-xs font-bold" style={{ color: '#1B4F8A' }}>{h.label}</p>
              </div>
            ))}
          </div>
        )}

        <button data-testid="onboarding-next" onClick={() => {
          if (step < 2) setStep(step + 1);
          else completeOnboarding();
        }}
          className="btn-press w-full py-4 rounded-full font-extrabold text-white text-base flex items-center justify-center gap-2"
          style={{ background: '#FF6B35', boxShadow: '0 4px 0 #C04918' }}>
          {step === 2 ? (<><Sparkles size={18} /> Start Practicing!</>) : (<>Continue <ArrowRight size={18} /></>)}
        </button>
      </div>
    </div>
  );
}
